import { AuthCard } from "@/components/auth/AuthCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { authClient } from "@/lib/auth/client";
import { Eye, EyeOff, Lock } from "lucide-react";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";

export default function ResetPasswordPage() {
  const navigate = useNavigate();

  const token = React.useMemo(() => {
    const url = new URL(window.location.href);
    const queryToken = url.searchParams.get("token");
    if (queryToken) return queryToken;

    const parts = url.pathname.split("/").filter(Boolean);
    const last = parts[parts.length - 1];
    return last && last !== "reset-password" ? last : "";
  }, []);

  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [validatingToken, setValidatingToken] = React.useState(Boolean(token));
  const [isTokenValid, setIsTokenValid] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    const validateToken = async () => {
      if (!token) {
        if (!cancelled) {
          setIsTokenValid(false);
          setValidatingToken(false);
          setError("Reset token is missing.");
        }
        return;
      }

      setValidatingToken(true);
      try {
        const callbackURL = `${window.location.origin}/auth/reset-password`;
        const response = await fetch(
          `/api/auth/reset-password/${encodeURIComponent(token)}?callbackURL=${encodeURIComponent(callbackURL)}`,
          { credentials: "include" },
        );
        const finalURL = new URL(response.url);
        const tokenError = finalURL.searchParams.get("error");
        const redirectedToken = finalURL.searchParams.get("token");

        if (!cancelled) {
          if (tokenError || !redirectedToken) {
            setIsTokenValid(false);
            setError("This reset link is invalid or expired. Request a new password reset email.");
          } else {
            setIsTokenValid(true);
          }
        }
      } catch {
        if (!cancelled) {
          setIsTokenValid(false);
          setError("Unable to validate reset link. Please reload the page and try again.");
        }
      } finally {
        if (!cancelled) {
          setValidatingToken(false);
        }
      }
    };

    void validateToken();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (validatingToken) {
      return;
    }

    if (!isTokenValid) {
      setError("This reset link is invalid or expired. Request a new password reset email.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const result = await authClient.resetPassword({
        newPassword: password,
        token,
      });

      if (result?.error) {
        setError(result.error.message ?? "Unable to reset password.");
        return;
      }

      navigate("/auth/login", {
        replace: true,
        state: { message: "Password reset complete. Sign in with your new password." },
      });
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard
      title="Set a new password"
      description="Choose a strong password for your account."
      footer={
        <p>
          Back to{" "}
          <Link className="text-primary font-medium hover:underline" to="/auth/login">
            sign in
          </Link>
        </p>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <div className="space-y-2">
          <Label htmlFor="password">New password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="At least 8 characters"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
              className="pl-9 pr-9"
            />
            <button
              type="button"
              tabIndex={-1}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm new password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              id="confirmPassword"
              type={showPassword ? "text" : "password"}
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
              className="pl-9"
            />
          </div>
        </div>

        {validatingToken ? (
          <p aria-live="polite" className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
            Validating reset link...
          </p>
        ) : null}

        {error ? (
          <p role="alert" aria-live="polite" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <Button type="submit" className="w-full" disabled={loading || validatingToken || !isTokenValid || password.length < 8}>
          {loading ? <Spinner size="sm" className="text-primary-foreground" /> : null}
          <span translate="no">{loading ? "Resetting..." : "Reset password"}</span>
        </Button>
      </form>
    </AuthCard>
  );
}
